import React, { Component } from 'react';
import PropTypes from 'prop-types';
import handleKeyPress from '../../common/handleKeyPress';
import HorizontalRule from '../HorizontalRule';
import * as Styled from './styles';

const MAX_VALUE_MESSAGE = 'Maximum value is 50';
const MIN_VALUE_MESSAGE = 'Minimum value is 0';

const Input = ({ id, icon, paddingTop, paddingBottom, onChange, border }) => {
    const value = paddingBottom || paddingTop;

    const onUpClick = (event) => {
        const newValue = paddingTop ? paddingTop + 1 : paddingBottom + 1;
        event.target.value = newValue;
        onChange(event);
    };

    const onDownClick = (event) => {
        const newValue = paddingTop ? paddingTop - 1 : paddingBottom - 1;
        event.target.value = newValue;
        onChange(event);
    };

    return (
        <Styled.InputDiv data-test-id={`${id}-input`} border={border}>
            <Styled.InputTextDiv>
                <Styled.IconDiv>
                    <i className="material-icons" style={{ fontSize: 16, color: '#424242' }}>
                        {icon}
                    </i>
                </Styled.IconDiv>
                <label htmlFor={id}>
                    <input
                        type="number"
                        min={0}
                        max={50}
                        id={id}
                        style={{
                            border: 'none',
                            textAlign: 'center',
                            outline: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            color: '#424242',
                            minWidth: 0,
                            maxWidth: 19,
                            marginLeft: 4,
                            paddingTop: 0,
                        }}
                        onChange={(event) => {
                            onChange(event);
                        }}
                        value={value.toString()}
                    />
                </label>
                <Styled.PXDiv>px</Styled.PXDiv>
            </Styled.InputTextDiv>
            <Styled.ArrowDiv>
                <i
                    className="material-icons inc"
                    data-test-id={`${id}-arrow-up`}
                    style={{ fontSize: 15, color: '#727273', cursor: 'pointer' }}
                    onClick={(e) => onUpClick(e)}
                    onKeyDown={(e) => handleKeyPress(e, () => onUpClick(e))}
                    tabIndex={0}
                    role="button"
                    aria-label={`increase ${id}`}
                >
                    keyboard_arrow_up
                </i>
                <HorizontalRule styling={{ margin: 0 }} />
                <i
                    className="material-icons dec"
                    data-test-id={`${id}-arrow-down`}
                    style={{ fontSize: 15, color: '#727273', cursor: 'pointer' }}
                    onClick={(e) => onDownClick(e)}
                    onKeyDown={(e) => handleKeyPress(e, () => onDownClick(e))}
                    tabIndex={0}
                    role="button"
                    aria-label={`decrease ${id}`}
                >
                    keyboard_arrow_down
                </i>
            </Styled.ArrowDiv>
        </Styled.InputDiv>
    );
};

export default class BlockPadding extends Component {
    state = {
        linked: false,
        errors: {
            paddingTop: '',
            paddingBottom: '',
        },
    };

    handleChange = (event, type) => {
        const { onChange } = this.props;
        const { errors } = this.state;
        const { value } = event.target;
        if (value > 50) {
            const linkedErrors = { paddingTop: MAX_VALUE_MESSAGE, paddingBottom: MAX_VALUE_MESSAGE };
            const noLinkErrors = { [type]: MAX_VALUE_MESSAGE };
            const newErrors = type === 'linked' ? { ...errors, ...linkedErrors } : { ...errors, ...noLinkErrors };
            this.setState({ errors: newErrors });
            return;
        }
        if (value < 0) {
            const linkedErrors = { paddingTop: MIN_VALUE_MESSAGE, paddingBottom: MIN_VALUE_MESSAGE };
            const noLinkErrors = { [type]: MIN_VALUE_MESSAGE };
            const newErrors = type === 'linked' ? { ...errors, ...linkedErrors } : { ...errors, ...noLinkErrors };
            this.setState({ errors: newErrors });
            return;
        }
        const clearErrors = { ...errors };
        Object.keys(clearErrors).forEach((val) => {
            clearErrors[val] = '';
        });
        this.setState({ errors: clearErrors });
        onChange(event, type);
    };

    handleOnClick = () => {
        this.setState((prevState) => ({ linked: !prevState.linked }));
    };

    render() {
        const { paddingTop, paddingBottom } = this.props;
        const { linked, errors } = this.state;

        return (
            <div style={{ position: 'relative' }}>
                <Styled.BlockPaddingDiv>
                    <div>
                        <Styled.Label>Top</Styled.Label>
                        <Input
                            id="padding-top"
                            icon="vertical_align_top"
                            paddingTop={paddingTop}
                            border={errors.paddingTop ? '1px solid #E82E41' : undefined}
                            onChange={(event) => this.handleChange(event, linked ? 'linked' : 'paddingTop')}
                        />
                        <Styled.Label>Bottom</Styled.Label>
                        <Input
                            id="padding-bottom"
                            icon="vertical_align_bottom"
                            paddingBottom={paddingBottom}
                            border={errors.paddingBottom ? '1px solid #E82E41' : undefined}
                            onChange={(event) => this.handleChange(event, linked ? 'linked' : 'paddingBottom')}
                        />
                    </div>
                    <Styled.LinkedButtonDiv>
                        <Styled.LineDiv pos="Top" />
                        <div
                            onClick={this.handleOnClick}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyPress(e, this.handleOnClick)}
                            role="button"
                            data-test-id="link-block-padding"
                            aria-pressed={linked}
                            aria-label={`${linked ? 'Unlink' : 'Link'} top and bottom block spacing`}
                        >
                            <Styled.LockIcon linked={linked ? 1 : 0} icon={linked ? 'link' : 'unlink'} />
                        </div>
                        <Styled.LineDiv pos="Bottom" />
                    </Styled.LinkedButtonDiv>
                </Styled.BlockPaddingDiv>
                {errors.paddingTop && <Styled.PaddingTopError>{errors.paddingTop}</Styled.PaddingTopError>}
                {errors.paddingBottom && <Styled.PaddingBottomError>{errors.paddingBottom}</Styled.PaddingBottomError>}
            </div>
        );
    }
}

BlockPadding.propTypes = {
    onChange: PropTypes.func.isRequired,
    paddingTop: PropTypes.number.isRequired,
    paddingBottom: PropTypes.number.isRequired,
};

Input.propTypes = {
    id: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    paddingTop: PropTypes.number,
    paddingBottom: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    border: PropTypes.string,
};

Input.defaultProps = {
    paddingTop: 0,
    paddingBottom: 0,
    border: '1px solid #CCCCCC',
};
